import { useTranslation } from "@refinedev/core";

import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Button, Paper, Stack, Typography } from "@mui/material";

import type { EditPresenceSession } from "@/types/edit-presence";

interface EditPresenceBannerProps {
  otherEditors: EditPresenceSession[];
  isStale?: boolean;
  staleEditorName?: string;
  onRefresh?: () => void;
}

export const EditPresenceBanner = ({ otherEditors, isStale, staleEditorName, onRefresh }: EditPresenceBannerProps) => {
  const { translate } = useTranslation();

  if (!isStale && otherEditors.length === 0) {
    return null;
  }

  if (isStale) {
    return (
      <Stack alignItems="center" width="100%">
        <Paper elevation={0} variant="alertError" data-testid="edit-presence-stale-banner">
          <Stack spacing={2} alignItems="center">
            <Typography variant="body2" textAlign="center" color="text.primary">
              {staleEditorName
                ? translate("editPresence.recordUpdatedBy", { userName: staleEditorName })
                : translate("editPresence.recordUpdated")}
            </Typography>
            {onRefresh && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                data-testid="edit-presence-refresh-button"
              >
                {translate("editPresence.refresh")}
              </Button>
            )}
          </Stack>
        </Paper>
      </Stack>
    );
  }

  const displayNames = otherEditors.map(e => e.userName).join(", ");

  return (
    <Stack alignItems="center" width="100%">
      <Paper elevation={0} variant="alertError" data-testid="edit-presence-concurrent-banner">
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <WarningAmberIcon color="error" fontSize="small" />
          <Typography variant="body2" color="text.primary">
            {translate("editPresence.concurrentEditor", { userName: displayNames })}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
};
